import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { notification } from 'antd';

import AssetInventoryV2Context from '@totem/components/services/assetInventory/v2/AssetInventoryV2Context';
import {
  FINDING_LABELS,
  formatCalendarDay,
  formatDay,
} from '@totem/components/services/assetInventory/v2/checks';
import {
  DETAILS_ONLY_FILTERS,
  getSystemTypeLabel,
} from '@totem/components/services/assetInventory/v2/configuration';
import {
  AssetInventoryDevices,
  AssetInventorySummary,
  TabKey,
} from '@totem/components/services/assetInventory/v2/types';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params } from '@totem/types/common';
import { isNotNull } from '@totem/utilities/common';
import {
  ASSET_INVENTORY_V2_ENDPOINT,
  DEVICES_ENDPOINT,
} from '@totem/utilities/endpoints';
import { exportRecordsToCsv } from '@totem/utilities/fileUtilities';

const FILTER_OPTION_KEYS = ['building', 'region', 'type', 'vendor'];

const SERVICE_LABELS = {
  none: 'None on file',
  expired: 'Expired',
  ending: 'Ending soon',
  active: 'Active',
};

type Props = {
  children?: ReactNode;
};

const AssetInventoryV2Container = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const {
    buildParameters,
    handleFilter,
    setFilterOptions,
    filterUpdateCount,
    pagingUpdateCount,
    sortingUpdateCount,
    isReady,
  } = useContext(InputContext);

  const [summary, setSummary] = useState<AssetInventorySummary>(null);
  const [summaryLoading, setSummaryLoading] = useState<boolean>(true);
  const [deviceData, setDeviceData] = useState<AssetInventoryDevices>(null);
  const [devicesLoading, setDevicesLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<TabKey>('summary');
  const [openChecks, setOpenChecks] = useState<string[]>([]);
  const [exporting, setExporting] = useState<boolean>(false);

  const post = (endpoint: string, params: Params) =>
    fetch(endpoint, {
      method: 'POST',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
      body: JSON.stringify(params),
    }).then((res) => {
      if (!res.ok) {
        throw new Error(`${res.status}`);
      }
      return res.json();
    });

  useEffect(() => {
    fetch(`${DEVICES_ENDPOINT}/filters`, {
      method: 'GET',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
    })
      .then((res) => res.json())
      .then((result) => {
        if (isNotNull(result)) {
          FILTER_OPTION_KEYS.forEach((key) => {
            if (isNotNull(result[key])) {
              setFilterOptions(
                key,
                result[key].map((opt) => ({
                  text: opt.name,
                  value: opt.value,
                })),
              );
            }
          });
        }
      });
  }, [userProfile.organization.id]);

  // The summary ignores the Details-only filters, so a stable key avoids refetching when only those change.
  const buildSummaryParameters = (): Params => {
    const params = buildParameters();
    return {
      ...params,
      filters: params.filters.filter(
        (filter) => !DETAILS_ONLY_FILTERS.includes(filter.field),
      ),
    };
  };
  const summaryKey = JSON.stringify(buildSummaryParameters().filters);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    setSummaryLoading(true);

    post(`${ASSET_INVENTORY_V2_ENDPOINT}/summary`, buildSummaryParameters())
      .then((result: AssetInventorySummary) => setSummary(result))
      .catch(() =>
        notification.error({
          title: 'Asset Inventory',
          description: 'The asset inventory summary could not be loaded.',
        }),
      )
      .finally(() => setSummaryLoading(false));
  }, [summaryKey, isReady, userProfile.organization.id]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    setDevicesLoading(true);

    post(`${ASSET_INVENTORY_V2_ENDPOINT}/devices`, buildParameters())
      .then((result: AssetInventoryDevices) => setDeviceData(result))
      .catch(() =>
        notification.error({
          title: 'Asset Inventory',
          description: 'The asset list could not be loaded.',
        }),
      )
      .finally(() => setDevicesLoading(false));
  }, [
    filterUpdateCount,
    pagingUpdateCount,
    sortingUpdateCount,
    isReady,
    userProfile.organization.id,
  ]);

  const toggleCheck = (key: string) =>
    setOpenChecks((prev) =>
      prev.includes(key) ? prev.filter((chk) => chk !== key) : [...prev, key],
    );

  const showCheck = (key: string) => {
    setOpenChecks((prev) => (prev.includes(key) ? prev : [...prev, key]));

    setActiveTab('flagged');

    setTimeout(
      () =>
        document
          .getElementById(`asset-inventory-check-${key}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
      100,
    );
  };

  const showDetails = (finding?: string) => {
    handleFilter('finding', isNotNull(finding) ? [finding] : []);

    setActiveTab('details');
  };

  // Exports every asset matching the current filters, not just the visible page.
  const exportCsv = () => {
    const params = buildParameters();
    const total = deviceData?.total ?? 0;

    setExporting(true);

    post(`${ASSET_INVENTORY_V2_ENDPOINT}/devices`, {
      ...params,
      paging: { page: 1, pageSize: Math.max(total, 1) },
    })
      .then((result: AssetInventoryDevices) => {
        exportRecordsToCsv(
          'asset-inventory',
          [
            'Name',
            'Type',
            'Vendor',
            'Operating System',
            'Firmware',
            'IP Addresses',
            'Region',
            'Building',
            'Building System',
            'System Type',
            'Managed',
            'Last Backup',
            'Past End of Support',
            'End of Support',
            'Service Contract',
            'Service Ends',
            'Findings',
          ],
          result.devices.map((device) => [
            device.name,
            device.type,
            device.vendor,
            device.operatingSystem,
            device.firmware,
            device.ipAddresses.join(' '),
            device.region?.name ?? '',
            device.building?.name ?? '',
            device.controlSystem?.name ?? '',
            isNotNull(device.systemType)
              ? getSystemTypeLabel(device.systemType)
              : '',
            device.isManaged ? 'Yes' : 'No',
            device.lastBackup ? formatDay(device.lastBackup) : '',
            device.pastEndOfSupport ? 'Yes' : 'No',
            device.endOfSupport ? formatCalendarDay(device.endOfSupport) : '',
            SERVICE_LABELS[device.serviceStatus] ?? '',
            device.serviceEnd ? formatCalendarDay(device.serviceEnd) : '',
            device.findings
              .map((finding) => FINDING_LABELS[finding] ?? finding)
              .join('; '),
          ]),
        );
      })
      .catch(() =>
        notification.error({
          title: 'Asset Inventory',
          description: 'The export could not be created.',
        }),
      )
      .finally(() => setExporting(false));
  };

  return (
    <AssetInventoryV2Context.Provider
      value={{
        summary,
        summaryLoading,
        devices: deviceData?.devices ?? [],
        devicesLoading,
        totalDevices: deviceData?.total ?? 0,
        activeTab,
        setActiveTab,
        openChecks,
        toggleCheck,
        showCheck,
        showDetails,
        exportCsv,
        exporting,
      }}
    >
      {children}
    </AssetInventoryV2Context.Provider>
  );
};

export default AssetInventoryV2Container;
